/**
 * Handles the selection of items, clears last selected, control select, mouse select, shift mouse select are included
 * @param {Object} params - The parameters object.
 * @param {Array} params.items - The items to be selected.
 * @param {Object} params.selection - The selection object containing site, index, rangeSelect, multiSelect, and event.
 * @param {Array} params.currentSelected - The current selected items.
 * @param {Function} params.getItemId - The function to get the id of an item.
 * @param {number} params.lastSelectedIndex - The last selected index.
 * @returns {Object} The updated selected items and last index.
 */
export const handleSelection = ({ items, selection, currentSelected, getItemId, lastSelectedIndex}) => {
    const { site: item, index, rangeSelect, multiSelect} = selection;
    const isSelected = currentSelected.some(selected => getItemId(selected) === getItemId(item));
    let newSelected = [...currentSelected];

    if (rangeSelect) {
        const {newSelected: rangeSelected, newLastIndex: rangeLastIndex} = _rangeSelect(
            items, 
            index, 
            lastSelectedIndex, 
            newSelected,
            getItemId
        );
        return {newSelected: rangeSelected, newLastIndex: rangeLastIndex};
    }

    if (multiSelect) {
        const {newSelected: multiSelected} = _multiSelect(
            isSelected, 
            newSelected, 
            item, 
            getItemId
        );
        return {newSelected: multiSelected, newLastIndex: index};
    }
    
    return {newSelected: [item], newLastIndex: index};
}

/**
 * Handles the multi selection of items.
 * @param {boolean} isSelected - Whether the item is selected.
 * @param {Array} newSelected - The new selected items.
 * @param {Object} item - The item to be selected.
 * @param {Function} getItemId - The function to get the id of an item.
 * @returns {Object} The updated selected items.
 */
const _multiSelect = (isSelected, newSelected, item, getItemId) => {
    let updatedSelected = [...newSelected];

    if (isSelected) {
        updatedSelected = newSelected.filter(selected => getItemId(selected) !== getItemId(item));
    } else {
        updatedSelected.push(item);
    }

    return {newSelected: updatedSelected};
}

/**
 * Handles the range selection of items.
 * @param {Array} items - The items to be selected.
 * @param {number} index - The index of the item.
 * @param {number} lastSelectedIndex - The last selected index.
 * @param {Array} newSelected - The new selected items.
 * @param {Function} getItemId - The function to get the id of an item.
 * @returns {Object} The updated selected items and last index.
 */
const _rangeSelect = (items, index, lastSelectedIndex, newSelected, getItemId) => {
    const startIndex = Math.min(lastSelectedIndex || 0, index);
    const endIndex = Math.max(lastSelectedIndex || 0, index);

    let updatedSelected = [...newSelected];

    const rangeItems = items.slice(startIndex, endIndex + 1);
    rangeItems.forEach(item => {
        if (!updatedSelected.some(selected => getItemId(selected) === getItemId(item))) {
            updatedSelected.push(item);
        }
    });
    
    let updatedIndex = index;

    return {newSelected: updatedSelected, newLastIndex: updatedIndex};
}